import { Injectable, inject } from '@angular/core';
import { Resolve, Router, ActivatedRouteSnapshot } from '@angular/router';
import { UnifiedSurveyStore } from './unified-survey.store';
import { SurveyLoginType } from '@hyper-inferno/survey-elements-core-utils';
import * as Sentry from '@sentry/angular'; // Recommended to import as Sentry
import { getBrowserFingerPrint } from '../utils/browser-finger-print';
import { APP_CONTEXT } from '@hyper-inferno/hyper-base-ng';



@Injectable({ providedIn: 'root' })
export class SurveyResolver implements Resolve<boolean> {
  private store = inject(UnifiedSurveyStore);
  private router = inject(Router);
  private app_context = inject(APP_CONTEXT)

  // private db = new OfflineSurveyDb();

  async resolve(route: ActivatedRouteSnapshot): Promise<boolean> {
    const params = route.params;
    const queryParams = route.queryParams;
    const currentPath = route.url.map((s) => s.path).join('/');

    try {
      // --- PHASE 1: OFFLINE/LOCAL CHECK ---
      const hasLocalData = await this.checkLocalAvailability(params);

      if (hasLocalData) {
        return await this.initializeOfflineMode(params, queryParams);
      }

      // --- PHASE 2: ONLINE/REMOTE CHECK ---
      // if (navigator.onLine) {
      return await this.initializeOnlineMode(params, {
        ...queryParams,
        browser_fp: getBrowserFingerPrint(),
        app_context: this.app_context
      });
      // }

      // --- PHASE 3: FALLBACK TO OFFLINE NOTICE ---
      this.redirectToOffline(currentPath, queryParams);
      return false;
    } catch (error: any) {
      // 1. Log to console for local debugging
      Sentry.captureException(
        new Error(
          `Survey Resolver Failed: User was looking for a missing resource. Original error: ${error.message}`,
        ),
      );

      // 2. Redirect the user
      this.router.navigate(['/not-found']);
      return false
    }
  }

  /**
   * Checks IndexedDB to see if we can fulfill this request without internet.
   */
  private async checkLocalAvailability(params: any): Promise<boolean> {
    // if (params.sessionId) {
    //   const session = await this.db.sessions.get(params.sessionId);
    //   return !!session;
    // }
    // if (params.surveyId || params.invitationId) {
    //   const surveyDef = await this.db.surveys.get(
    //     Number(params.surveyId || params.invitationId),
    //   );
    //   return !!surveyDef;
    // }
    return false;
  }

  /**
   * Strategy: Start/Resume from IndexedDB
   */
  private async initializeOfflineMode(
    params: any,
    query: any,
  ): Promise<boolean> {
    console.log('Strategy: Offline Resume');
    // await this.store.startSurveyTransaction();
    return true;
  }

  /**
   * Strategy: Handshake with NestJS & Connect to Bot
   */
  private async initializeOnlineMode(
    params: any,
    query: any,
  ): Promise<boolean> {
    console.log('Strategy: Online Handshake');

    const { sessionId, invitationId, surveyId } = params;

    let loginType: SurveyLoginType;

    // Determine Entry Point
    if (sessionId) {
      loginType = SurveyLoginType.SESSION;
    } else if (invitationId) {
      loginType = SurveyLoginType.INVITATION;
    } else if (surveyId) {
      loginType = SurveyLoginType.SELF_SERVE;
    } else {
      this.router.navigate(['/not-found']);
      return false;
    }

    // Identifies the entry point for the Store/BotService
    const payload = {
      sessionId: params.sessionId,
      invitationId: params.invitationId,
      surveyId: Number(params.surveyId),
      loginType,
      params: query
    };

    await this.store.startSurveyTransaction(payload);

    return true;
  }

  /**
   * Redirects to Offline Notice with "Return URL" for the Retry button
   */
  private redirectToOffline(path: string, query: any) {
    this.router.navigate(['/offline-notice'], {
      queryParams: { returnUrl: `/${path}`, ...query },
    });
  }
}
